/**
 * ProductListContainer
 * --
 */
import { useState, useReducer, useEffect, useMemo } from 'react';
import ProductListPresenter from './ProductListPresenter';
import MessageAlert from 'utils';
import {
  useDeleteProduct,
  useUpdateProduct,
  useUpsertProduct,
} from 'hooks/ProductHooks';
import { Content, Row, Col, ModalLayout } from 'components';
import { Button } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { decrypt, encrypt } from '../../../../utils/Crypto';

// URL에 저장된 암호화 매장 번호를 안전하게 숫자로 변환합니다.
const getShopInformationFromQuery = (shopQuery) => {
  if (!shopQuery) return null;

  try {
    const shopInformationId = Number(decrypt(shopQuery));

    return Number.isFinite(shopInformationId) && shopInformationId > 0
      ? shopInformationId
      : null;
  } catch (error) {
    console.error('[ProductList] Invalid shop query parameter.', error);
    return null;
  }
};

// 상품 검색 필터의 기본값과 URL 쿼리 이름을 한곳에서 관리합니다.
const PRODUCT_FILTER_DEFAULTS = {
  productType: 'product_name',
  salesType: 'all',
  applicationType: 'all',
  type: 'all',
  keyword: '',
  dates: ['', ''],
  updates: ['', ''],
  exhibition: 'all',
};

// 상품 검색에 사용하는 URL 쿼리만 선별하여 관리합니다.
const PRODUCT_FILTER_QUERY_KEYS = [
  'searchType',
  'keyword',
  'salesType',
  'applicationType',
  'productType',
  'createdStart',
  'createdEnd',
  'updatedStart',
  'updatedEnd',
  'exhibition',
];

// 허용된 선택값만 URL에서 복원하여 잘못된 쿼리가 필터에 들어가지 않게 합니다.
const getAllowedQueryValue = (searchParams, key, allowedValues, fallback) => {
  const value = searchParams.get(key);

  return allowedValues.includes(value) ? value : fallback;
};

// URL 쿼리를 상품 검색 컴포넌트에서 사용하는 필터 객체로 변환합니다.
const getProductFilterFromQuery = (searchParams) => {
  const hasFilterQuery = PRODUCT_FILTER_QUERY_KEYS.some((key) =>
    searchParams.has(key),
  );

  if (!hasFilterQuery) return null;

  return {
    productType: getAllowedQueryValue(
      searchParams,
      'searchType',
      ['product_name', 'product_code', 'product_category'],
      PRODUCT_FILTER_DEFAULTS.productType,
    ),
    salesType: getAllowedQueryValue(
      searchParams,
      'salesType',
      ['all', 'sales_wait', 'sales', 'sold_out', 'sales_stop', 'sales_end'],
      PRODUCT_FILTER_DEFAULTS.salesType,
    ),
    applicationType: getAllowedQueryValue(
      searchParams,
      'applicationType',
      ['all', 'apply', 'reject', 'approve', 'wait'],
      PRODUCT_FILTER_DEFAULTS.applicationType,
    ),
    type: getAllowedQueryValue(
      searchParams,
      'productType',
      ['all', 'delivery', 'rental', 'subscription'],
      PRODUCT_FILTER_DEFAULTS.type,
    ),
    keyword: searchParams.get('keyword') ?? PRODUCT_FILTER_DEFAULTS.keyword,
    dates: [
      searchParams.get('createdStart') ?? '',
      searchParams.get('createdEnd') ?? '',
    ],
    updates: [
      searchParams.get('updatedStart') ?? '',
      searchParams.get('updatedEnd') ?? '',
    ],
    exhibition: getAllowedQueryValue(
      searchParams,
      'exhibition',
      ['all', 'best', 'new'],
      PRODUCT_FILTER_DEFAULTS.exhibition,
    ),
  };
};

/**----------------------------------------
 * [Component] 총관리자: 상품목록 - 컨테이너
 * ----------------------------------------
 */
const ProductListContainer = ({ defaultShopId, permission }) => {
  /* ===== INITIAL ===== */
  const queryClient = useQueryClient();
  const {
    deleteProduct,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useDeleteProduct();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageQuery = Number(searchParams.get('page'));
  const currentPage =
    Number.isInteger(pageQuery) && pageQuery > 0 ? pageQuery : 1;
  const shopInformation = getShopInformationFromQuery(searchParams.get('shop'));
  const currentShop = shopInformation ?? defaultShopId ?? null;
  const pagenationInformation = currentPage;
  // URL을 검색 결과의 기준으로 사용하여 새로고침 후에도 필터를 복원합니다.
  const searchInfo = useMemo(
    () => getProductFilterFromQuery(searchParams),
    [searchParams],
  );
  // console.log('permission ====>', permission);

  /* ===== Mutate ===== */
  const { updateProduct } = useUpdateProduct();
  const { upsertProduct } = useUpsertProduct();

  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  // 삭제 컨펌 모달
  const [isOpenDeleteConfirm, setIsOpenDeleteConfirm] = useState(false);
  // 페이징
  // 전체 선택
  const [isCheckedAll, setIsCheckedAll] = useReducer((state, action) => {
    switch (action.type) {
      case 'TOGGLE':
        return !state;
      case 'RESET':
        return false;
      default:
        return state;
    }
  }, false);
  // 선택 목록
  const [checkedList, setCheckedList] = useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**--------------------
   * 페이징 처리
   * --------------------
   */
  const handlePagination = (page) => {
    if (currentPage === page) return;

    // 페이지 이동 시 현재 선택된 매장 쿼리를 함께 유지합니다.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(page));
    if (currentShop) nextParams.set('shop', encrypt(currentShop));

    setSearchParams(nextParams);
  };

  /**--------------------
   * 업체 변경 처리
   * --------------------
   */
  const handleCurrentShop = (shopInformationId) => {
    if (!shopInformationId) return;
    if (Number(shopInformationId) === Number(currentShop)) return;

    // 업체를 변경하면 해당 업체의 첫 페이지부터 조회합니다.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', '1');
    nextParams.set('shop', encrypt(shopInformationId));

    setSearchParams(nextParams);
  };

  /**--------------------
   * 상품 검색 필터 URL 반영
   * --------------------
   */
  const handleSearchProduct = (filter) => {
    // 검색 또는 초기화 시 기존 필터 쿼리를 제거하고 첫 페이지로 이동합니다.
    const nextParams = new URLSearchParams(searchParams);
    PRODUCT_FILTER_QUERY_KEYS.forEach((key) => nextParams.delete(key));
    nextParams.set('page', '1');

    if (filter) {
      if (filter.productType !== PRODUCT_FILTER_DEFAULTS.productType) {
        nextParams.set('searchType', filter.productType);
      }
      if (filter.keyword) nextParams.set('keyword', filter.keyword);
      if (filter.salesType !== PRODUCT_FILTER_DEFAULTS.salesType) {
        nextParams.set('salesType', filter.salesType);
      }
      if (filter.applicationType !== PRODUCT_FILTER_DEFAULTS.applicationType) {
        nextParams.set('applicationType', filter.applicationType);
      }
      if (filter.type !== PRODUCT_FILTER_DEFAULTS.type) {
        nextParams.set('productType', filter.type);
      }
      if (filter.dates?.[0]) {
        nextParams.set('createdStart', filter.dates[0]);
      }
      if (filter.dates?.[1]) nextParams.set('createdEnd', filter.dates[1]);
      if (filter.updates?.[0]) {
        nextParams.set('updatedStart', filter.updates[0]);
      }
      if (filter.updates?.[1]) nextParams.set('updatedEnd', filter.updates[1]);
      if (filter.exhibition !== PRODUCT_FILTER_DEFAULTS.exhibition) {
        nextParams.set('exhibition', filter.exhibition);
      }
    }

    setSearchParams(nextParams);
  };

  /**--------------------
   * 체크박스 핸들러
   * --------------------
   * @param {string} type
   * @param {object | array} obj
   */
  const handleChecked = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setCheckedList((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setCheckedList((prev) =>
          prev.filter((select) => select.key !== obj.key),
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setCheckedList(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = checkedList.filter((user) =>
          obj.some((ob) => ob.key === user.key),
        );

        setCheckedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = checkedList.length === obj.length;

        if (!isAll) {
          setCheckedList(obj);
        } else {
          setCheckedList([]);
        }
        return;
      default:
        return;
    }
  };

  /**--------------------
   *
   * --------------------
   */
  const handleConfirmDeleteProduct = () => {
    if (checkedList.length === 0) {
      MessageAlert.warning('삭제하실 상품을 선택해주세요.');
      return;
    } else if (checkedList.length > 1) {
      MessageAlert.warning('1건만 삭제 가능합니다.');
      return;
    }

    setIsOpenDeleteConfirm(true);
  };

  /**--------------------
   *
   * --------------------
   */
  const handleDeleteProduct = async () => {
    if (checkedList.length === 1) {
      const response = await deleteProduct(checkedList[0].key);

      if (response && response.status === 200) {
        queryClient.resetQueries({ queryKey: ['product'] });
        MessageAlert.success('상품을 삭제하였습니다.');
      } else {
        MessageAlert.warning('상품 삭제에 실패하였습니다.');
      }

      setIsOpenDeleteConfirm(false);
    }
  };

  /**
   * 상품 진열 상태 변경
   * --
   * @param {number} idx
   * @param {string} key
   * @param {string} value
   * @param {array} arr
   * @param {func} callback
   */
  const handleToggleProduct = async (idx, key, value, arr = [], callback) => {
    const msg1 =
      key === 'is_best'
        ? '베스트 상품'
        : key === 'is_new'
          ? '신상품'
          : '브랜드';
    const msg2 = value ? '진열' : '진열 해제';

    if (permission !== 'ADMIN') {
      MessageAlert.info(`${msg1} ${msg2}은/는 총관리자만 가능합니다`);
      return;
    }

    try {
      const { status } = await updateProduct({
        product_id: idx,
        [key]: value ? 'Y' : 'N',
      });

      if (status !== 200) {
        MessageAlert.success(`${msg1}에 ${msg2} 실패하였습니다.`);
        return;
      }

      const fid = arr.findIndex((item) => item.product_id === idx);

      if (fid !== -1) {
        callback((prev) => [
          ...prev.slice(0, fid),
          { ...prev[fid], [key]: value ? 'Y' : 'N' },
          ...prev.slice(fid + 1),
        ]);
      }

      MessageAlert.success(`${msg1}에 ${msg2}되었습니다.`);
    } catch (err) {
      MessageAlert.error('상품 진열에 실패하였습니다.');
      console.error('[handleToggleProduct] Error!! ', err);
    }
  };

  /**
   * 상품 Bulk 수정
   * --
   * @param {string} key
   * @param {string} value
   * @param {string} message
   */
  const handleBulkProduct = async (key, value, message = null) => {
    if (!checkedList.length) {
      MessageAlert.info('변경할 상품을 선택해주세요!');
      return;
    }

    const confirm = (msg = '') => window.confirm(msg);

    try {
      if (
        !confirm(
          `${checkedList.length}개의 상품 ${
            message ?? '상태'
          }를 변경하시겠습니까?`,
        )
      )
        return;

      const updateData = checkedList.map((product) => ({
        product_id: product.key,
        [key]: value,
      }));

      const { status } = await upsertProduct(updateData);

      if (status !== 200) {
        MessageAlert.info(
          `${checkedList.length}개의 상품 ${
            message ?? '상태'
          } 수정에 실패하였습니다.`,
        );
        return;
      }

      queryClient.resetQueries({ queryKey: ['product'] });
      MessageAlert.success(
        `${checkedList.length}개의 상품 ${message ?? '상태'}가 수정되었습니다.`,
      );
      setCheckedList([]);
    } catch (err) {
      console.error(`[handleBulkProduct] Error!! ${err}`);
      MessageAlert.error('에러발생!');
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  /* ===== HOOKS ===== */
  /**--------------------
   * 최초 진입 시 기본 페이지와 매장 정보를 URL에 반영
   * --------------------
   */
  useEffect(() => {
    const hasValidPage = searchParams.get('page') === String(currentPage);
    const hasValidShop = !currentShop || shopInformation === currentShop;

    if (hasValidPage && hasValidShop) return;

    // 새로고침 후에도 동일 조건을 복원할 수 있도록 누락된 쿼리를 보완합니다.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(currentPage));
    if (currentShop) nextParams.set('shop', encrypt(currentShop));

    setSearchParams(nextParams, { replace: true });
  }, [
    currentPage,
    currentShop,
    searchParams,
    setSearchParams,
    shopInformation,
  ]);

  /**--------------------
   * 페이지 변경 시 체크박스 초기화
   * --------------------
   */
  useEffect(() => {
    setIsCheckedAll({ type: 'RESET' });
    setCheckedList([]);
  }, [currentPage]);

  /**--------------------
   * 업체 변경 시 체크박스 초기화
   * --------------------
   */
  useEffect(() => {
    setIsCheckedAll({ type: 'RESET' });
    setCheckedList([]);
  }, [currentShop]);

  /**--------------------
   *
   * --------------------
   */
  useEffect(() => {
    if (isDeleteSuccess) {
      return;
    }

    if (isDeleteError) {
      MessageAlert.error('상품 삭제에 실패하였습니다.');
      return;
    }
  }, [isDeleteError, isDeleteSuccess]);

  /* ===== RENDER ===== */
  // JSX: start
  return (
    <>
      <ProductListPresenter
        // State
        loading={loading}
        defaultShopId={defaultShopId}
        permission={permission}
        currentPage={currentPage}
        isCheckedAll={isCheckedAll}
        checkedList={checkedList}
        currentShop={currentShop}
        searchInfo={searchInfo}
        listQuery={searchParams.toString()}
        // Function
        setCurrentShop={handleCurrentShop}
        onSearch={handleSearchProduct}
        onPagination={handlePagination}
        onChecked={handleChecked}
        onCheckedAll={setIsCheckedAll}
        onDelete={handleConfirmDeleteProduct}
        onToggle={handleToggleProduct}
        setLoading={setLoading}
        onBulk={handleBulkProduct}
        pagenationInformation={pagenationInformation}
      />
      <ModalLayout
        type={'modal'}
        open={isOpenDeleteConfirm}
        onCancel={() => setIsOpenDeleteConfirm(false)}
        centered
        closable
        title={'상품을 삭제 하시겠습니까?'}
        width={300}
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Row>
              <Col x={24}>
                <Button onClick={() => setIsOpenDeleteConfirm(false)}>
                  취소
                </Button>
                &nbsp;
                <Button
                  type="primary"
                  onClick={() => {
                    handleDeleteProduct();
                  }}
                >
                  삭제
                </Button>
              </Col>
            </Row>
          </Content>
        }
      />
    </>
  ); // JSX: end
};

ProductListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default ProductListContainer;
